import { JSONTree } from 'packages/react-json-tree/src';
import React, {
  ComponentProps,
  Dispatch,
  MouseEvent,
  PropsWithChildren,
  SetStateAction,
  useState,
  useEffect
} from 'react';
import styled from 'styled-components';
import Papa from 'papaparse';
import { DataExtractorViewProps, pageIndexType } from './DataExtractorViewer';
import { JSONNestedNodeProps } from 'packages/react-json-tree/src/JSONNestedNode';
import { ObjType, ViewerContainer, ViewerHeader } from './ThumbnailViewer';
import { Button, Typography } from 'design-component';
import { palette } from 'styles/color';
import { IconDownload, IconCopy, IconFilter, IconExpand } from 'assets/icon';
import IconCheckWhite from 'assets/icon/check-white.svg';
import TooltipMenu from './TooltipMenu';
import { useTranslation } from 'react-i18next';
import ga, { EventResultCopy, EventResultDownload, EventResultFilter } from 'util/gaConnect';
import { useAppDispatch, useAppSelector } from 'store/store';
import { activeToast } from 'store/slices/toastSlice';
import { Dialog, DialogActions, DialogContent, DialogTitle } from 'design-component/Dialog';
import CsvTable from './CsvTable';
import { unzip, ZipEntry } from 'unzipit';
import { zip } from 'fflate';
import { languageSelector } from 'store/slices/languageSlice';
import { userInfoSelector } from 'store/slices/userInfo';
import type { JsonType, SampleType } from 'pages/Playground/DocExtractor';
import { getSampleArchiveType } from 'data/sample';
import Dropdown from './Dropdown';

interface JsonViewerProps extends DataExtractorViewProps {
  data?: any;
  markdownData?: string;
  xmlData?: string;
  sampleData?: any;
  isLoading: boolean;
  highlight?: { ref: React.RefObject<HTMLDivElement | null>; width: number };
  pageIndex: pageIndexType;
  onChangePageIndex?: Dispatch<SetStateAction<pageIndexType>>;
  resFileBlob?: Blob;
  docExtension?: string;
  selectedType?: SampleType;
  imgData?: {
    fileName: string;
    url: string;
  }[];
  schemaType: JsonType;
  setSchemaType: Dispatch<SetStateAction<JsonType>>;
  fullJson?: any;
}

type ResultFormat = 'json' | 'markdown' | 'xml';

const RESULT_FILE_EXTENSION: Record<ResultFormat, string> = {
  json: 'json',
  markdown: 'md',
  xml: 'xml'
};

const IMAGE_FILE_PATTERN = /\.(png|jpe?g|gif|webp|svg|bmp|tiff?)$/i;

const FilterValue = [
  { label: 'All select', key: 'all' },
  { label: 'Text', key: 'text' },
  { label: 'image', key: 'image' },
  { label: 'chart', key: 'chart' },
  { label: 'table', key: 'table' },
  { label: 'shape', key: 'shape' }
] as const;

type FilterKeyType = (typeof FilterValue)[number]['key'];

const JsonViewer = (props: JsonViewerProps) => {
  const { t } = useTranslation();
  const {
    file,
    highlight,
    pageIndex,
    onChangePageIndex,
    data,
    markdownData,
    xmlData,
    isLoading,
    resFileBlob,
    sampleData,
    docExtension,
    selectedType,
    imgData,
    schemaType,
    setSchemaType,
    fullJson
  } = props;
  const [filter, setFilter] = useState<FilterKeyType[]>(FilterValue.map((item) => item.key));
  const [dialogContent, setDialogContent] = useState<{
    fileName?: string;
    src?: string;
    csv?: string;
  } | null>(null);
  const [isNestedModalOpen, setIsNestedModalOpen] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const lang = useAppSelector(languageSelector) as 'en' | 'ko';
  const [isHwp, setIsHwp] = useState(false);
  const [resultFormat, setResultFormat] = useState<ResultFormat>('json');

  const { isAdmin } = useAppSelector(userInfoSelector);

  // const handleJsonTypeChange = (type: JsonType) => {
  //   setSchemaType(type);
  // };

  useEffect(() => {
    setIsHwp(
      !!(selectedType === 'hwp' || (docExtension && ['hwp', 'hwpx'].includes(docExtension)))
    );
  }, [docExtension, selectedType]);

  const isSheet =
    (docExtension && ['xls', 'xlsx'].includes(docExtension)) || selectedType === 'sheet';

  useEffect(() => {
    if (dialogContent?.csv) {
      const parsedData = Papa.parse(dialogContent.csv.trim(), {
        header: true,
        skipEmptyLines: true
      });

      setHeaders(parsedData.meta.fields || []);
      setRows(parsedData.data);
    }
  }, [dialogContent]);

  const handleCloseDialog = () => {
    setDialogContent(null);
  };

  const handleOpenNestedModal = () => {
    setIsNestedModalOpen(true); // 중첩 모달 열기
  };

  const handleCloseNestedModal = () => {
    setIsNestedModalOpen(false); // 중첩 모달 닫기
  };

  const downloadCSV = (csvData: string, fileName: string) => {
    const BOM = '\uFEFF'; // UTF-8 BOM : 인코딩 문제 해결
    const blob = new Blob([BOM + csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName; // 지정된 파일명 사용
    a.click();
    URL.revokeObjectURL(url);
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    setResultFormat('json');
  }, [file, selectedType]);

  const getSelectedResult = () => {
    if (resultFormat === 'markdown') return markdownData || '';
    if (resultFormat === 'xml') return xmlData || '';
    return JSON.stringify(filtered || data, null, 2);
  };

  const onCopy = async () => {
    try {
      ga.event(EventResultCopy);
      await navigator.clipboard.writeText(getSelectedResult());
      dispatch(
        activeToast({
          type: 'info',
          msg: `${t('toast.copiedToClipboard')}`
        })
      );
    } catch (err) {
      dispatch(
        activeToast({
          type: 'error',
          msg: `${t('toast.copyFailed')}`
        })
      );
    }
  };

  const createSelectedResultArchive = async () => {
    if (!resFileBlob) {
      throw new Error('Result archive is not available');
    }

    const extension = RESULT_FILE_EXTENSION[resultFormat];
    const archive = await unzip(resFileBlob);
    const entries = Object.values(archive.entries);
    const resultEntry = entries.find((entry) =>
      entry.name.toLowerCase().endsWith(`.${extension}`)
    );

    if (!resultEntry) {
      throw new Error(`${extension.toUpperCase()} result is not available`);
    }

    const selectedEntries = [
      resultEntry,
      ...entries.filter((entry) => entry !== resultEntry && IMAGE_FILE_PATTERN.test(entry.name))
    ];
    const files = Object.fromEntries(
      await Promise.all(
        selectedEntries.map(async (entry) => [
          entry.name.replace(/^\/+/, ''),
          new Uint8Array(await (await entry.blob()).arrayBuffer())
        ])
      )
    );

    const zippedData = await new Promise<Uint8Array>((resolve, reject) => {
      zip(files, { level: 6 }, (error, data) => {
        if (error) reject(error);
        else resolve(data);
      });
    });

    const zipBuffer = new ArrayBuffer(zippedData.byteLength);
    new Uint8Array(zipBuffer).set(zippedData);
    return new Blob([zipBuffer], { type: 'application/zip' });
  };

  const onDownload = async () => {
    ga.event(EventResultDownload);

    if (resFileBlob) {
      try {
        const resultArchive = await createSelectedResultArchive();
        const extension = RESULT_FILE_EXTENSION[resultFormat];
        const baseName =
          file?.name.replace(/\.[^.]+$/, '') ||
          sampleData?.docName?.replace(/\.[^.]+$/, '') ||
          'data-insight';
        const downloadUrl = window.URL.createObjectURL(resultArchive);
        const anchorElement = document.createElement('a');
        document.body.appendChild(anchorElement);
        anchorElement.href = downloadUrl;
        anchorElement.download = `${baseName}-${extension}.zip`;
        anchorElement.click();
        document.body.removeChild(anchorElement);
        window.URL.revokeObjectURL(downloadUrl);
        dispatch(activeToast({ type: 'info', msg: `${t('toast.downloadResultSuccess')}` }));
      } catch (err) {
        console.error('Error creating result archive:', err);
        dispatch(activeToast({ type: 'error', msg: `${t('toast.downloadResultFail')}` }));
      }
      return;
    }

    if (sampleData) {
      try {
        if (!selectedType || selectedType === 'upload') return;

        const sampleArchiveType = getSampleArchiveType(selectedType);
        const downloadUrl = `/sampleZip/${sampleArchiveType}-sample-${lang}.zip`;
        const fileName = `${sampleArchiveType}-sample-${lang}.zip`;

        const anchorElement = document.createElement('a');
        anchorElement.href = downloadUrl;
        anchorElement.download = fileName;
        document.body.appendChild(anchorElement);
        anchorElement.click();
        document.body.removeChild(anchorElement);

        dispatch(
          activeToast({
            type: 'info',
            msg: `${t('toast.downloadJsonSuccess')}`
          })
        );
      } catch (err) {
        dispatch(
          activeToast({
            type: 'error',
            msg: `${t('toast.downloadJsonFail')}`
          })
        );
      }
    }
  };

  const updateFilter = (type: FilterKeyType) => {
    if (type === 'all') {
      if (filter.length === FilterValue.length) {
        setFilter([]);
      } else {
        setFilter(FilterValue.map((item) => item.key));
      }
    } else {
      if (filter.includes(type)) {
        setFilter(filter.filter((value) => value !== type));
      } else {
        setFilter([...filter, type]);
      }
    }
  };

  const filtered = objectFilter(data, filter);

  const replaceFileExtension = (fileName: string, newExtension: string): string => {
    const fileNameWithoutExtension = fileName
      ?.split('.')
      .slice(0, -1) // 마지막 확장자 제외
      .join('.');

    return `${fileNameWithoutExtension}.${newExtension}`;
  };

  const handleCsvDownload = (csv: string | undefined, fileName: string | undefined) => {
    try {
      if (csv && fileName) {
        const csvFileName = replaceFileExtension(fileName, 'csv');
        downloadCSV(csv, csvFileName);
      }
    } catch (err) {
      console.error('Error downloading CSV:', err);
    }
  };

  useEffect(() => {
    // zip 파일에서 이미지를 가져오는 함수
    const fetchImageFromZip = async () => {
      if (!dialogContent?.fileName || !selectedType) return;
      if (selectedType === 'upload') return;

      const sampleArchiveType = getSampleArchiveType(selectedType);
      try {
        const zipFilePath = `/sampleZip/${sampleArchiveType}-sample-${lang}.zip`;

        const response = await fetch(zipFilePath);
        if (!response.ok) {
          throw new Error(`Failed to fetch zip file: ${zipFilePath}`);
        }

        const zipBlob = await response.blob();

        const zip = await unzip(zipBlob);
        const dataZipEntry = zip.entries['data.zip'];
        if (!dataZipEntry) {
          throw new Error('data.zip not found in the main zip file');
        }

        const dataZipBlob = await dataZipEntry.blob();

        const innerZip = await unzip(dataZipBlob);

        const targetFile = innerZip.entries[dialogContent.fileName];
        if (!targetFile) {
          console.error(`File not found in data.zip: ${dialogContent.fileName}`);
          setDialogContent((prev) => ({
            ...prev,
            src: undefined // 파일이 없을 경우 undefined
          }));
          return;
        }

        const fileBlob = await targetFile.blob();
        const correctedBlob = new Blob([fileBlob], { type: 'image/png' });
        const urlBlob = URL.createObjectURL(correctedBlob);

        setDialogContent((prev) => ({
          ...prev,
          src: urlBlob
        }));
      } catch (error) {
        console.error('Error fetching image from zip:', error);
        setDialogContent((prev) => ({
          ...prev,
          src: undefined // 에러 발생 시 undefined
        }));
      }
    };

    if (dialogContent?.src === undefined && dialogContent?.fileName) {
      fetchImageFromZip();
    }
  }, [dialogContent, selectedType]);

  const getCsvFileName = (fileName: string | undefined): string => {
    if (!fileName) return 'table.csv';
    return isSheet ? `${fileName}.csv` : replaceFileExtension(fileName, 'csv');
  };

  const handleSchemaChange = (_type: JsonType) => {
    setSchemaType(_type);
  };

  const selectedTextResult =
    resultFormat === 'markdown' ? markdownData : resultFormat === 'xml' ? xmlData : undefined;
  const hasSelectedResult = resultFormat === 'json' ? !!data : !!selectedTextResult;

  return (
    <JsonViewerBase>
      <ViewerHeader style={{ padding: '0px 10px' }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <ResultFormatButton
            disabled={!data}
            color="primary"
            $active={resultFormat === 'json'}
            onClick={() => setResultFormat('json')}>
            {t('json')}
          </ResultFormatButton>
          <ResultFormatButton
            disabled={!markdownData}
            color="primary"
            $active={resultFormat === 'markdown'}
            onClick={() => setResultFormat('markdown')}>
            {t('markdown')}
          </ResultFormatButton>
          <ResultFormatButton
            disabled={!xmlData}
            color="primary"
            $active={resultFormat === 'xml'}
            onClick={() => setResultFormat('xml')}>
            {t('xml')}
          </ResultFormatButton>
          {/* {isAdmin && selectedType !== 'pdf' && (
            <Button color="primary" disabled={!data} onClick={() => handleJsonTypeChange('full')}>
              JSON - Full Schema
            </Button>
          )} */}
          {isAdmin && resultFormat === 'json' && (
            <Dropdown
              options={[
                { value: 'unified', label: 'Unified' },
                { value: 'full', label: 'Full' }
              ]}
              value={schemaType}
              onChange={(value) => handleSchemaChange(value as JsonType)}
              headerStyle={{
                width: '102px'
              }}
              listStyle={{
                width: '140px'
              }}
            />
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
          <Button
            startIcon={<IconCopy />}
            color="primary"
            onClick={onCopy}
            disabled={!hasSelectedResult || (resultFormat === 'json' && schemaType === 'full')}>
            {t('copy')}
          </Button>
          <Button
            color="primary"
            startIcon={<IconDownload />}
            onClick={onDownload}
            disabled={!hasSelectedResult || (resultFormat === 'json' && schemaType === 'full')}>
            {t('download')}
          </Button>
        </div>
      </ViewerHeader>
      {/* NOTE: 객체 필터 옵션 영역 숨김 */}
      {/* <ViewerHeader style={{ height: '50px', padding: '0px 10px' }}>
        <div></div>
        <div>
          <TooltipMenu
            opener={
              <Button
                color="primary"
                disabled={!data || schemaType === 'full'}
                startIcon={<IconFilter></IconFilter>}>
                {t('filter')}
              </Button>
            }
            onOpen={() => ga.event(EventResultFilter)}>
            <Typography
              component="p"
              size="12px"
              weight={500}
              height="16px"
              color={palette.gray[500]}
              style={{ padding: '10px' }}>
              Filter
            </Typography>
            {FilterValue.map((type) => (
              <React.Fragment key={type.key}>
                <TooltipMenu.MenuItem key={type.key} onClick={() => updateFilter(type.key)}>
                  <FilterTooltipItem>
                    <Checkbox
                      checked={
                        type.key === 'all'
                          ? filter.length === FilterValue.length // all checked 
                          : filter.includes(type.key)
                      }
                      onChange={() => {}} // React 경고 제거를 위한 빈 함수
                      readOnly
                    />
                    <Typography size="14px" weight={400} height="16px">
                      {t(type.key)}
                    </Typography>
                  </FilterTooltipItem>
                </TooltipMenu.MenuItem>
                {type.key === 'all' && (
                  <div
                    style={{
                      width: '100%',
                      height: '1px',
                      padding: '0px 10px',
                      backgroundColor: palette.gray[200]
                    }}></div>
                )}
              </React.Fragment>
            ))}
          </TooltipMenu>
        </div>
      </ViewerHeader> */}
      <Body>
        {isLoading ? (
          <Loading></Loading>
        ) : resultFormat !== 'json' && selectedTextResult ? (
          <TextResult aria-label={`${resultFormat} result`}>{selectedTextResult}</TextResult>
        ) : filtered ? (
          <ViewerContent>
            {isAdmin && schemaType === 'full' ? (
              <JSONTree
                theme={{
                  base00: palette.white,
                  base09: '#CC6633',
                  base0B: '#805AD5',
                  base0D: '#0397EA',

                  tree: ({ style }) => ({
                    style: {
                      ...style,
                      fontSize: '10px'
                    }
                  })
                }}
                shouldExpandNodeInitially={() => true}
                data={fullJson}></JSONTree>
            ) : (
              <JSONTree
                theme={{
                  base00: palette.white,
                  base09: '#CC6633',
                  base0B: '#805AD5',
                  base0D: '#0397EA',
                  tree: ({ style }) => ({
                    style: {
                      ...style,
                      fontSize: '10px'
                    }
                  })
                }}
                shouldExpandNodeInitially={() => true}
                data={filtered}
                CustomJSONNestedNode={(props) => (
                  <DocObject
                    docExtension={docExtension}
                    selectedType={selectedType}
                    file={file}
                    json={filtered}
                    highlight={highlight}
                    viewIndex={pageIndex}
                    onChangeViewIndex={onChangePageIndex}
                    isFiltered={
                      filter.length !== FilterValue.length && filter.includes('all') === false
                    }
                    setDialogContent={setDialogContent}
                    imgData={imgData}
                    {...props}></DocObject>
                )}></JSONTree>
            )}
          </ViewerContent>
        ) : (
          <Initial></Initial>
        )}
      </Body>
      <Dialog
        // onClose={handleCloseDialog}
        isOpen={dialogContent !== null}
        style={{
          width: '900px',
          maxHeight: '782px'
        }}>
        <DialogTitle
          style={{
            padding: '30px 32px 0px'
          }}>
          <Typography
            weight={700}
            size="18px"
            height="20px"
            style={{
              marginBottom: '4px'
            }}>
            {t('viewModal.viewData').toLocaleUpperCase()}
          </Typography>
          <Typography weight={400} size="14px" height="20px" color={palette.gray[500]}>
            {isSheet ? t('viewModal.viewDataTableDesc') : t('viewModal.viewDataChartDesc')}
          </Typography>
        </DialogTitle>
        <DialogContent
          style={{
            padding: '0 20px'
          }}>
          {dialogContent?.src && (
            <div className="chartObject">
              <Typography weight={600} size="16px" height="16px" color={palette.gray[800]}>
                {t('viewModal.chartObject')}
              </Typography>
              <div className="title">
                <Typography
                  component="span"
                  weight={400}
                  size="14px"
                  height="16px"
                  color={palette.gray[700]}
                  truncate>
                  {dialogContent.fileName}
                </Typography>
                <Button
                  color="primary"
                  startIcon={<IconDownload />}
                  onClick={() => {
                    try {
                      const downloadUrl = dialogContent.src || '';
                      const a = document.createElement('a');
                      a.href = downloadUrl;
                      a.download = `${dialogContent.fileName}`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);

                      dispatch(
                        activeToast({
                          type: 'info',
                          msg: `${t('toast.chartImageSuccess')}`
                        })
                      );
                    } catch (err) {}
                  }}>
                  {t('download')}
                </Button>
              </div>
              <div
                style={{ width: '100%', height: '100%', minHeight: '188px' }}
                className="imgPreview">
                <img
                  src={dialogContent.src}
                  alt="chart image"
                  onClick={() => {
                    try {
                      const previewUrl = dialogContent.src || '';
                      window.open(previewUrl, '_blank');
                    } catch (err) {
                      console.error('Error opening image preview:', err);
                    }
                  }}
                />
              </div>
            </div>
          )}

          <div className="tableData">
            {dialogContent?.csv && (
              <>
                <Typography weight={600} size="16px" height="16px" color={palette.gray[800]}>
                  {t('viewModal.tableData')}
                </Typography>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  className="title">
                  <Typography
                    component="span"
                    weight={400}
                    size="14px"
                    height="16px"
                    color={palette.gray[700]}
                    truncate>
                    {getCsvFileName(dialogContent.fileName)}
                  </Typography>
                  <Button
                    color="primary"
                    startIcon={<IconDownload />}
                    onClick={() => {
                      try {
                        handleCsvDownload(
                          dialogContent?.csv,
                          getCsvFileName(dialogContent.fileName)
                        );
                        dispatch(
                          activeToast({
                            type: 'info',
                            msg: `${t('toast.tableCsvSuccess')}`
                          })
                        );
                      } catch (err) {}
                    }}>
                    {t('download')}
                  </Button>
                </div>
                <div className="tablePreview">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: `1px solid ${palette.gray[200]}`,
                      padding: '0 8px 6px',
                      height: '36px'
                    }}>
                    <Typography
                      component="div"
                      weight={600}
                      size="14px"
                      height="16px"
                      color={palette.gray[800]}>
                      {t('viewModal.preview')}
                    </Typography>
                    {rows.length > 4 && (
                      <Button
                        color="primary"
                        // disabled={rows.length <= 4} NOTE: AS-IS는 disabled 처리
                        onClick={handleOpenNestedModal}
                        style={{
                          width: '28px',
                          height: '28px',
                          padding: '7.25px',
                          borderRadius: '8px',
                          visibility: rows.length === 0 ? 'hidden' : 'visible'
                        }}>
                        <IconExpand />
                      </Button>
                    )}
                  </div>
                  <div className="tableContents">
                    {(
                      isHwp
                        ? rows.length > 99 || headers.length > 100 // HWP 파일의 경우 100*100 제한
                        : rows.length > 199 || headers.length > 100
                    ) ? (
                      <div className="row-overflow-style">
                        <Typography
                          component="div"
                          weight={500}
                          size="14px"
                          height="20px"
                          color={palette.gray[700]}>
                          {t('viewModal.previewTooLarge')}
                        </Typography>
                        <Typography
                          component="div"
                          weight={700}
                          size="14px"
                          height="20px"
                          color={palette.gray[700]}>
                          {lang === 'ko' ? 'CSV 파일을 ' : 'Please '}
                          <DownloadLink
                            text={t('download')}
                            onClick={() =>
                              handleCsvDownload(
                                dialogContent?.csv,
                                getCsvFileName(dialogContent?.fileName)
                              )
                            }
                          />
                          {lang === 'ko' ? ' 받아 확인해주세요.' : ' the CSV file.'}
                        </Typography>
                      </div>
                    ) : (
                      <CsvTable headers={headers.slice(0, 10)} rows={rows.slice(0, 3)} />
                    )}
                  </div>
                </div>
                <NestedDialog onClose={handleCloseNestedModal} isOpen={isNestedModalOpen}>
                  <div className="title">
                    <Typography
                      weight={400}
                      size="14px"
                      height="16px"
                      color={palette.gray[700]}
                      truncate>
                      {getCsvFileName(dialogContent.fileName)}
                    </Typography>
                    <Button
                      color="primary"
                      startIcon={<IconDownload />}
                      onClick={() => {
                        try {
                          handleCsvDownload(
                            dialogContent?.csv,
                            getCsvFileName(dialogContent.fileName)
                          );
                          dispatch(
                            activeToast({
                              type: 'info',
                              msg: `${t('toast.tableCsvSuccess')}`
                            })
                          );
                        } catch (err) {}
                      }}>
                      {t('download')}
                    </Button>
                  </div>
                  <div className="expandTableView">
                    <CsvTable headers={headers.slice(0, 200)} rows={rows.slice(0, 99)} />
                  </div>
                </NestedDialog>
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions
          style={{
            padding: '20px 0'
          }}>
          <Button
            color="primary"
            onClick={handleCloseDialog}
            style={{
              width: '360px'
            }}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </JsonViewerBase>
  );
};

const NestedDialog = (props: {
  onClose: () => void;
  isOpen: boolean;
  children: React.ReactNode;
}) => {
  const { onClose, isOpen, children } = props;
  const { t } = useTranslation();

  return (
    <Dialog
      // onClose={onClose}
      isOpen={isOpen}
      style={{
        width: '1000px',
        maxHeight: '800px'
      }}>
      <DialogTitle
        style={{
          padding: '30px 20px 0px'
        }}>
        {t('viewModal.tableDataDetails')}
      </DialogTitle>
      <DialogContent
        style={{
          padding: '0 20px'
        }}>
        {children}
      </DialogContent>
      <DialogActions
        style={{
          padding: '20px 0'
        }}>
        <Button
          color="primary"
          onClick={onClose}
          style={{
            width: '360px'
          }}>
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JsonViewer;

const DownloadLink = ({ text, onClick }: { text: string; onClick: () => void }) => (
  <u style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={onClick}>
    {text}
  </u>
);

const ResultFormatButton = styled(Button).withConfig({
  shouldForwardProp: (prop) => prop !== '$active'
})<{ $active: boolean }>`
  background-color: ${({ $active }) => ($active ? palette.white : palette.gray[100])};
  color: ${({ $active }) => ($active ? palette.blue[500] : palette.gray[800])};
  border: 1px solid ${({ $active }) => ($active ? palette.blue[500] : palette.gray[200])};

  &:hover {
    background: ${palette.white};
  }

  &:active {
    background: ${palette.white};
    color: ${palette.gray[800]} !important;
  }

  &:disabled {
    background: ${palette.gray[200]};
    border-color: ${palette.gray[200]};
    color: ${palette.gray[400]} !important;
  }
`;

const JsonViewerBase = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 8px 0;
  }

  .chartObject {
    padding: 20px 20px 10px;

    .imgPreview {
      width: 100%;
      height: 220px;
      padding: 10px 12px;
      box-sizing: border-box;
      background: rgba(248, 248, 248, 1);

      img {
        height: 165px;
        width: fit-content;
        cursor: pointer;
      }
    }
  }

  .tableData {
    padding: 0 20px;
    margin-top: 20px;

    .tablePreview {
      width: 100%;
      height: 220px;
      padding: 8px 12px;
      box-sizing: border-box;
      background: rgba(248, 248, 248, 1);

      display: flex;
      flex-direction: column;
      gap: 8px;

      .tableContents {
        // margin: 24px 20px;
        // height: calc(100% - 44px);
        // max-height: 114px;
        overflow: auto;
        flex: 1;

        .row-overflow-style {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
      }
    }

    .expandTableView {
      width: 100%;
      height: 100%;
      background: rgba(248, 248, 248, 1);
      padding: 8px 12px;
      max-height: 618px;
      overflow: auto;
    }
  }
`;

const Body = styled(ViewerContainer)`
  flex: 1;
  overflow: auto;
`;

const ViewerContent = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex;
  justify-content: flex-start;
`;

const TextResult = styled.pre`
  width: 100%;
  min-height: 100%;
  margin: 0;
  padding: 16px;
  box-sizing: border-box;
  overflow: auto;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${palette.gray[800]};
  background: ${palette.white};
  font-family: monospace;
  font-size: 12px;
  line-height: 1.6;
`;

const Initial = () => {
  const { t } = useTranslation();
  return (
    <Typography size="14px" weight={500} color={palette.gray[400]} align="center">
      {t('panelDesc')}
    </Typography>
  );
};

const Loading = () => {
  const { t } = useTranslation();
  return (
    <Typography size="14px" weight={500} color={palette.gray[400]} align="center">
      {t('docProcessingCompleteMsg')}
      <br />
      {t('extractedJsonResultsMsg')}{' '}
    </Typography>
  );
};

interface DocObjectProps extends Pick<JSONNestedNodeProps, 'keyPath' | 'data'> {
  json: JSONNestedNodeProps['data'];
  highlight: JsonViewerProps['highlight'];
  viewIndex: JsonViewerProps['pageIndex'];
  onChangeViewIndex: JsonViewerProps['onChangePageIndex'];
  isFiltered: boolean;
  file: File | undefined;
  docExtension: string | undefined;
  selectedType: JsonViewerProps['selectedType'];
  resFileBlob: Blob;
  setDialogContent: Dispatch<
    SetStateAction<{ fileName?: string; src?: string; csv?: string } | null>
  >;
  imgData: JsonViewerProps['imgData'];
}

type PageInfoType = {
  docName: string;
  pageIndex: number;
  pageSize: { width: number; height: number };
  extractionSummary: Record<ObjType, number>;
  fileType: string;
};

const DocObject = (props: PropsWithChildren<DocObjectProps>) => {
  const { t } = useTranslation();
  const {
    docExtension,
    file,
    json,
    data,
    keyPath,
    highlight,
    viewIndex,
    onChangeViewIndex,
    isFiltered,
    selectedType,
    setDialogContent,
    resFileBlob,
    imgData
  } = props;
  const [isHovered, setIsHovered] = useState(false); // hover 상태 관리

  const isSheet =
    selectedType === 'sheet' || (docExtension && ['xls', 'xlsx'].includes(docExtension));

  const getPageInfo = (): PageInfoType | undefined => {
    try {
      // debugger;
      const doc = json as any;

      const docName = doc.docName;

      const pagesIndex = keyPath.findIndex((value) => value === 'pages');
      if (pagesIndex <= 0) return undefined;

      const pageIndex = keyPath.at(pagesIndex - 1) as number; // pageIndex
      const targetDoc = doc.pages[pageIndex]; // pageIndex으로 타겟 문서 가져옴
      if (!targetDoc) return undefined; // 유효하지 않은 페이지 처리

      const pageNum = targetDoc.pageNum; // targetDoc에서 pageNum 추출
      const width = targetDoc.pageWidth;
      const height = targetDoc.pageHeight;
      const extractionSummary = targetDoc.extractionSummary;

      const fileType = selectedType === 'upload' ? docExtension ?? '' : selectedType ?? '';

      return {
        docName: docName,
        pageIndex: pageNum,
        pageSize: { width, height },
        extractionSummary,
        fileType
      };
    } catch (err) {
      return undefined;
    }
  };

  const renderHighlight = ({
    highlight,
    pageSize,
    fileType
  }: Required<Pick<JsonViewerProps, 'highlight'>> & PageInfoType) => {
    const rect = (data as any).boundaryBox;
    // if (!rect) return;
    const highlightWidth = highlight.width;
    const highlightElement = highlight.ref.current;
    const pageWidth = pageSize.width;
    const pageHeight = pageSize.height;

    if (highlightElement) {
      if (fileType === 'pdf') {
        highlightElement.style.visibility = 'visible';
        const originalWidth = highlightElement.parentElement?.offsetWidth ?? pageWidth;
        const originalHeight = highlightElement.parentElement?.offsetHeight ?? pageHeight;
        const widthRatio = originalWidth / pageWidth;
        const heightRatio = originalHeight / pageHeight;

        highlightElement.style.left = `${rect.left * widthRatio}px`;
        highlightElement.style.top = `${rect.top * heightRatio}px`;
        highlightElement.style.width = `${(rect.right - rect.left) * widthRatio}px`;
        highlightElement.style.height = `${(rect.bottom - rect.top) * heightRatio}px`;
      } else {
        highlightElement.style.visibility = 'visible';
        highlightElement.style.left = `${(rect.left * highlightWidth) / pageWidth}px`;
        highlightElement.style.top = `${(rect.top * highlightWidth) / pageWidth}px`;
        highlightElement.style.width = `${
          ((rect.right - rect.left) * highlightWidth) / pageWidth
        }px`;
        highlightElement.style.height = `${
          ((rect.bottom - rect.top) * highlightWidth) / pageWidth
        }px`;
      }
    }
  };

  // const toTwip = (value: number) => {
  //   const dpi = 72;
  //   const twip = 1440;

  //   return (value / dpi) * twip;
  // };

  const onMouseOver = (e: MouseEvent<HTMLDivElement>) => {
    const { type } = data as any;

    const pageInfo = getPageInfo();
    // debugger;

    if (data) {
      // const isChart = type === 'chart';
      const isChart = type === 'chart' && !isSheet;
      const isSheetTable = isSheet && type === 'table';

      if (isChart || isSheetTable) {
        setIsHovered(true);
      }
    }

    try {
      e.stopPropagation();

      const highlightElement = highlight?.ref.current;

      if (highlightElement && pageInfo?.pageIndex === viewIndex.index + 1) {
        renderHighlight({ highlight, ...pageInfo });
      }
    } catch (err) {}
  };
  const onMouseOut = () => {
    setIsHovered(false); // hover 상태 비활성화
    try {
      const highlightElement = highlight?.ref.current;
      if (highlightElement) {
        highlightElement.style.visibility = 'hidden';
      }
    } catch (err) {}
  };

  const onClick = () => {
    try {
      const pageInfo = getPageInfo();

      // if (pageInfo?.fileType === 'sheet') return;

      if (highlight && pageInfo) {
        renderHighlight({ highlight, ...pageInfo });
        onChangeViewIndex?.({ index: pageInfo.pageIndex - 1 });
      }
    } catch (err) {}
  };

  const checkToWrap = () => {
    try {
      const check = data as any;
      return (
        Object.hasOwn(check as object, 'boundaryBox') &&
        Object.hasOwn((check as any).boundaryBox, 'left') &&
        Object.hasOwn((check as any).boundaryBox, 'top')
      );
    } catch (err) {}
  };
  const toWrap = checkToWrap();

  return toWrap ? (
    <DocObjectBase onMouseOver={onMouseOver} onMouseOut={onMouseOut} onClick={onClick}>
      <ButtonWrapper
        isVisible={isHovered}
        onClick={() => {
          const { id } = data as any;
          const { src, csv } = (data as any).content;
          const pageInfo = getPageInfo();

          let imageUrl: string | undefined;

          if (src && imgData) {
            const image = imgData.find((img: { fileName: string }) => img.fileName === src);
            imageUrl = image?.url; // imgData에서 타겟 파일명으로 url 찾기
          }

          if (imageUrl || csv) {
            setDialogContent({
              fileName: isSheet ? `${pageInfo?.docName ?? 'unknown'}_table(${id})` : src,
              src: imageUrl,
              csv
            });
          }
        }}>
        {t('viewModal.viewData')}
      </ButtonWrapper>
      {props.children}
    </DocObjectBase>
  ) : (
    <>{props.children}</>
  );
};

const DocObjectBase = styled.div`
  position: relative;
  &:hover {
    background-color: #418dff33;
  }
`;

const ButtonWrapper = styled.button.withConfig({
  shouldForwardProp: (prop) => !['isVisible'].includes(prop as string)
})<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 120px;
  height: 40px;
  background-color: #418dff;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  z-index: 1;
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;

  &:hover {
    background-color: #0056b3;
  }
`;

interface CheckboxProps extends ComponentProps<'input'> {}
const Checkbox = (props: CheckboxProps) => {
  return <CheckboxBase type="checkbox" {...props}></CheckboxBase>;
};

const CheckboxBase = styled.input`
  width: 18px;
  height: 18px;
  margin: 0px;
  cursor: pointer;

  appearance: none;
  border-radius: 6px;
  border: 1px solid ${palette.gray[400]};
  background-color: ${palette.white};

  &:checked {
    border-radius: 6px;
    border: 1px solid ${palette.blue[500]};
    background: url(${IconCheckWhite as string}) no-repeat center center;
    background-color: ${palette.blue[500]};
  }
`;

const FilterTooltipItem = styled.div`
  width: 180px;
  height: 38px;
  padding: 0px 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;

  user-select: none;
`;
const objectFilter = (obj: any, filter: FilterKeyType[]) => {
  if (!obj) return null;

  const allKeys = FilterValue.map((item) => item.key).filter((key) => key !== 'all');
  if (filter.includes('all') && allKeys.every((key) => filter.includes(key))) {
    return obj;
  }

  const filteredPages = obj?.pages
    ?.map((page: any) => {
      const filteredElements = page.elements.filter((item: any) =>
        filter.includes(item.type as FilterKeyType)
      );
      return {
        pageNum: page.pageNum,
        pageWidth: page.pageWidth,
        pageHeight: page.pageHeight,
        elements: filteredElements
      };
    })
    .filter((page: any) => page.elements.length > 0);

  return {
    ...obj,
    pages: filteredPages
  };
};
